import * as React from 'react';
import {
    createStyles,
    rem,
    Grid,
    Group,
    Input,
    Box,
    Accordion,
    Badge,
    Text,
    ActionIcon,
    useMantineTheme
} from '@mantine/core';
import { useId, useFocusWithin, useClickOutside, useMediaQuery } from '@mantine/hooks';
import { useTranslation, Trans } from 'react-i18next';
import { NumericFormat, NumberFormatValues } from 'react-number-format';
import { IconAlertCircleFilled, IconUser } from '@tabler/icons-react';

// Components
import { FieldLabelTooltip } from '../FieldLabelTooltip';
import { WageRangeChart } from './components/WageRangeChart';
import { AssignedWorkerLabel } from './components/AssignedWorkerLabel';

// Hooks
import { useFieldTranslation } from '../../hooks/useFieldTranslation';
import { useRangeLabel } from '../../../../hooks/useRangeLabel';

// Styles
import { getCommonFieldStyles } from '../../helpers/fieldStyles';

// Types
import { GenericFieldProps } from '../../types';
import { ISalaryRange } from '../../../../types';

import { rangeColors, rangeColorNames } from './colors';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: {
            ...commonFieldStyles.input,
            width: '8rem'
        },
        readonlyInput: commonFieldStyles.readonlyInput,
        label: {
            ...commonFieldStyles.label,
            fontSize: '0.9rem'
        },
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        inlineInputWrapper: commonFieldStyles.inputWrapperInline,
        description: commonFieldStyles.description,
        inlineLabel: {
            maxWidth: '23rem'
            // textAlign: 'right'
        },
        groupInputs: {
            flexWrap: 'nowrap'
        },
        track: {
            display: 'inline-block',
            height: '8px',
            width: '90%',
            margin: '0 5%',
            marginTop: '2rem',
            marginBottom: '4rem'
        },
        tick: {
            '&:before': {
                content: '""',
                position: 'absolute',
                left: '0',
                background: 'rgba(0, 0, 0, 0.2)',
                height: '5px',
                width: '2px',
                transform: 'translate(-50%, 0.7rem)'
            }
        },
        tickLabel: {
            position: 'absolute',
            fontSize: '0.6rem',
            color: 'rgba(0, 0, 0, 0.5)',
            top: '100%',
            transform: 'translate(-50%, 1.2rem)',
            whiteSpace: 'nowrap'
        },
        segment: {
            height: '100%',
            background: '#ff6050',
            '&[data-index="0"]': {
                background: rangeColors[0]
            },
            '&[data-index="1"]': {
                background: theme.colors.blue[4]
            },
            '&[data-index="2"]': {
                background: theme.colors.orange[4]
            },
            '&[data-index="3"]': {
                background: theme.colors.cyan[4]
            },
            '&[data-index="4"]': {
                background: theme.colors.cyan[5]
            },
            '&[data-index="5"]': {
                background: theme.colors.blue[5]
            }
        },
        noBottomPad: {
            paddingBottom: 0
        },
        rowInput: {
            '&:not(:focus)': {
                border: 'none',
                background: 'transparent'
            }
        },
        noBold: {
            fontWeight: 400
        },
        noMarginnoPadding: {
            margin: '0',
            padding: '0'
        },
        mark: {
            display: 'none'
        },

        markWrapper: {
            marginTop: rem(12)
        },

        thumb: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px%',
            fontSize: '0.7rem',
            whiteSpace: 'nowrap',
            width: rem(16),
            height: rem(28),
            backgroundColor: `${theme.white} !important`,
            color: theme.colors.gray[5],
            border: `${rem(1)} solid ${
                theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[3]
            } !important`
        },
        accordionLabel: {
            position: 'relative'
        },
        accordionColorBar: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '5px'
        },
        box: {
            // border: '1px solid #dee2e6',
            borderRadius: '6px',
            background: theme.colors.gray[1],
            marginTop: '1rem',
            marginBottom: '0.5rem',
            // padding: '1rem 1rem 0.25rem'
            padding: '0.5rem 0.5rem 0'
        },
        boxError: {
            background: theme.colors.red[1],
            border: `1px solid ${theme.colors.red[6]}`
        },
        boxHighlight: {
            background: theme.colors.orange[1],
            border: `1px solid ${theme.colors.orange[6]}`
        },
        groupInput: {
            width: '50%'
        },
        fullPartError: {
            position: 'absolute',
            bottom: '0',
            left: '23rem',
            marginLeft: '10px',
            marginBottom: '-7px'
        },
        accordionPanel: {
            borderTop: '1px solid #dee2e6',
            paddingBottom: '0.5rem'
        },
        graphAccordionLabel: {
            lineHeight: '1.4rem'
        },
        invisible: {
            pointerEvents: 'none',
            opacity: 0
        },
        userBadgeLeftSection: {
            marginRight: 0
        },
        divActionIcon: {
            cursor: 'pointer',
            padding: '0',
            appearance: 'none',
            fontSize: '1rem',
            textAlign: 'left',
            textDecoration: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            borderRadius: '0.25rem',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '1.75rem',
            minHeight: '1.75rem',
            width: '1.75rem',
            minEidth: '1.75rem',
            border: '0.0625rem solid transparent',
            backgroundColor: 'transparent',
            color: '#228be6'
        },
        divActionIconRed: {
            color: '#fa5252'
        }
    };
});

export interface CustomWageRangerProps extends GenericFieldProps {
    key?: string;
    error?: Record<string, string | null>;
    value?: ISalaryRange[];
    onChange?: (val: ISalaryRange[]) => void;
    readOnly?: boolean;
    required?: boolean;
    isContractors?: boolean;
}

const FocusWrapper: React.FC<{
    children: React.ReactNode;
    tier: number;
    setActive: React.Dispatch<React.SetStateAction<number | undefined>>;
}> = ({ children, tier, setActive }) => {
    const { ref, focused } = useFocusWithin();
    const { classes, cx } = useStyles();

    React.useEffect(() => {
        if (focused) {
            setActive(tier);
        }
    }, [focused]);

    return <Box ref={ref}>{children}</Box>;
};

export const WageRanger: React.FC<CustomWageRangerProps> = ({
    readOnly,
    labelKey,
    required,
    descriptionKey,
    tooltipKey,
    onChange,
    error,
    value,
    parentform,
    isContractors,
    targetperiod,
    highlight,
    ...props
}) => {
    const matches = useMediaQuery('(min-width: 768px)');
    const totalWorkers = isContractors
        ? parentform?.values?.contractorsWithHourlyRateCount
        : parentform?.values?.employeeCount;
    const [activeTier, setActiveTier] = React.useState<number | undefined>();
    const ref = useClickOutside(() => setActiveTier(undefined));
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const inputLabel = useFieldTranslation({ key: labelKey, formData: parentform?.values, targetPeriod: targetperiod });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const id = useId();
    const updateValue = (newVal: number, property: 'total' | 'ft' | 'pt', index: number) => {
        if (!value) return;
        const copy = [...value];

        copy[index][property] = newVal;

        onChange?.(copy);
        if (props.prefix) parentform?.setDirty({ [props.prefix]: true });
    };
    const { getRangeLabel } = useRangeLabel();
    const unassignedWorkers = React.useMemo(() => {
        const total = totalWorkers;
        const totalAllocatedEmployees = (value ?? []).reduce((acc, { total }) => (total ? acc + total : acc), 0);

        return total - totalAllocatedEmployees;
    }, [parentform?.values, value]);

    return (
        <>
            {/* {matches ? (
                <Box mb="lg">
                    <Accordion
                        variant="contained"
                        style={{ background: '#fff', borderRadius: '6px' }}
                        classNames={{
                            label: classes.graphAccordionLabel
                        }}
                    >
                        <Accordion.Item value={`wage-range-chart`}>
                            <Accordion.Control>{t('wageRangeChart.title', { ns: 'components' })}</Accordion.Control>
                            <Accordion.Panel className={classes.accordionPanel}>
                                <WageRangeChart
                                    data={value}
                                    maxValue={totalWorkers}
                                    activeTier={activeTier}
                                    setActiveTier={setActiveTier}
                                />
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </Box>
            ) : null} */}
            <Box ref={ref}>
                <Input.Wrapper
                    id={id}
                    label={
                        <>
                            <span>{inputLabel}</span>
                            {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                        </>
                    }
                    description={descriptionLabel}
                    required={required}
                    classNames={{
                        root: classes.root,
                        label: classes.label
                    }}
                    error={error?.total}
                >
                    <Box
                        className={cx(classes.box, {
                            [classes.boxError]: error?.total,
                            [classes.boxHighlight]: typeof highlight === 'string' && highlight === 'true'
                        })}
                    >
                        <Accordion variant="contained" style={{ background: '#fff' }}>
                            {(value ?? []).map(({ total, ft, pt, meta }, index) => {
                                const { totalFieldLabel, sectionLabel } = getRangeLabel(meta);

                                return (
                                    <Accordion.Item
                                        key={`range_${index}`}
                                        value={`range_${index}`}
                                        style={error?.[`range_${index}`] ? { background: '#fff1ef' } : {}}
                                    >
                                        <FocusWrapper tier={index} setActive={setActiveTier}>
                                            <Accordion.Control
                                            // icon={
                                            //     <Badge color={rangeColorNames[index]}>
                                            //         <Trans
                                            //             t={t}
                                            //             i18nKey={'wageRanger.rangeLabelPrefix'}
                                            //             ns="components"
                                            //             values={{
                                            //                 index: index + 1
                                            //             }}
                                            //         ></Trans>
                                            //     </Badge>
                                            // }
                                            >
                                                <Group position="apart">
                                                    <Text size="sm">{sectionLabel}</Text>
                                                    <Group>
                                                        {total ? (
                                                            <Badge
                                                                size="lg"
                                                                style={{
                                                                    paddingLeft: '5px'
                                                                }}
                                                                classNames={{
                                                                    leftSection: classes.userBadgeLeftSection
                                                                }}
                                                                leftSection={
                                                                    <div className={classes.divActionIcon}>
                                                                        <IconUser size={14} />
                                                                    </div>
                                                                }
                                                            >
                                                                {total}
                                                            </Badge>
                                                        ) : null}
                                                        <Box
                                                            className={cx('', {
                                                                [classes.invisible]: !error?.[`range_${index}`]
                                                            })}
                                                        >
                                                            <div
                                                                className={cx(classes.divActionIcon, {
                                                                    [classes.divActionIconRed]: true
                                                                })}
                                                            >
                                                                <IconAlertCircleFilled size={20} />
                                                            </div>
                                                        </Box>
                                                    </Group>
                                                </Group>
                                            </Accordion.Control>
                                            <Accordion.Panel className={classes.accordionPanel}>
                                                <Grid className={classes.accordionLabel}>
                                                    <Grid.Col span={12} style={{ paddingBottom: 0 }}>
                                                        <Input.Wrapper
                                                            label={t(
                                                                `${
                                                                    isContractors
                                                                        ? 'contractorCountWageTier'
                                                                        : 'employeeCountWageTier'
                                                                }.label`,
                                                                { ns: 'questions' }
                                                            )}
                                                            classNames={{
                                                                root: cx(classes.root, {
                                                                    [classes.inlineInputWrapper]: true
                                                                }),
                                                                error: classes.error,
                                                                label: cx(classes.label, {
                                                                    [classes.inlineLabel]: true
                                                                })
                                                            }}
                                                        >
                                                            <Input<any>
                                                                component={NumericFormat}
                                                                classNames={{
                                                                    input: cx(classes.input, {
                                                                        [classes.readonlyInput]: readOnly,
                                                                        [classes.noBottomPad]: true
                                                                    }),
                                                                    icon: classes.icon,
                                                                    wrapper: cx(classes.wrapper, {
                                                                        [classes.readonlyWrapper]: readOnly,
                                                                        [classes.inlineLabel]: true
                                                                    })
                                                                }}
                                                                disabled={readOnly}
                                                                error={error?.total}
                                                                onValueChange={(values: NumberFormatValues) => {
                                                                    updateValue(values.floatValue ?? 0, 'total', index);
                                                                }}
                                                                onFocus={() => setActiveTier(index)}
                                                                value={total}
                                                                allowNegative={false}
                                                                placeholder={t('wageRanger.total', {
                                                                    ns: 'components'
                                                                })}
                                                            />
                                                        </Input.Wrapper>
                                                    </Grid.Col>
                                                    <Grid.Col span={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                        <Input.Wrapper
                                                            classNames={{
                                                                root: cx(classes.root, {
                                                                    [classes.inlineInputWrapper]: true
                                                                }),
                                                                label: cx(classes.label, {
                                                                    [classes.inlineLabel]: true
                                                                }),
                                                                error: classes.fullPartError
                                                            }}
                                                            error={error?.[`range_${index}`]}
                                                        >
                                                            <Box className={classes.inlineLabel}>
                                                                <Input.Label>
                                                                    {t('employeesFullPartTime.label', {
                                                                        ns: 'questions'
                                                                    })}
                                                                </Input.Label>
                                                                <Input.Description>
                                                                    {t('employeesFullPartTime.description', {
                                                                        ns: 'questions'
                                                                    })}
                                                                </Input.Description>
                                                            </Box>
                                                            <Group className={classes.groupInputs}>
                                                                <Input<any>
                                                                    id={`full-time-${index}`}
                                                                    component={NumericFormat}
                                                                    classNames={{
                                                                        input: cx(classes.input, {
                                                                            [classes.readonlyInput]: readOnly,
                                                                            [classes.noBottomPad]: true
                                                                        }),
                                                                        icon: classes.icon,
                                                                        wrapper: cx(classes.wrapper, {
                                                                            [classes.readonlyWrapper]: readOnly
                                                                        })
                                                                    }}
                                                                    disabled={readOnly}
                                                                    error={error?.[`range_${index}`]}
                                                                    onValueChange={(values: NumberFormatValues) => {
                                                                        updateValue(
                                                                            values.floatValue ?? 0,
                                                                            'ft',
                                                                            index
                                                                        );
                                                                    }}
                                                                    allowNegative={false}
                                                                    onFocus={() => setActiveTier(index)}
                                                                    value={ft}
                                                                    placeholder={t('wageRanger.fullTime', {
                                                                        ns: 'components'
                                                                    })}
                                                                />
                                                                <Input<any>
                                                                    id={`part-time-${index}`}
                                                                    component={NumericFormat}
                                                                    classNames={{
                                                                        input: cx(classes.input, {
                                                                            [classes.readonlyInput]: readOnly,
                                                                            [classes.noBottomPad]: true
                                                                        }),
                                                                        icon: classes.icon,
                                                                        wrapper: cx(classes.wrapper, {
                                                                            [classes.readonlyWrapper]: readOnly
                                                                        })
                                                                    }}
                                                                    disabled={readOnly}
                                                                    error={error?.[`range_${index}`]}
                                                                    onValueChange={(values: NumberFormatValues) => {
                                                                        updateValue(
                                                                            values.floatValue ?? 0,
                                                                            'pt',
                                                                            index
                                                                        );
                                                                    }}
                                                                    allowNegative={false}
                                                                    onFocus={() => setActiveTier(index)}
                                                                    value={pt}
                                                                    placeholder={t('wageRanger.partTime', {
                                                                        ns: 'components'
                                                                    })}
                                                                />
                                                            </Group>
                                                        </Input.Wrapper>
                                                    </Grid.Col>
                                                </Grid>
                                            </Accordion.Panel>
                                        </FocusWrapper>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                        <Box py="sm" px="sm" style={{ textAlign: 'right' }}>
                            <AssignedWorkerLabel
                                isContractors={isContractors}
                                total={totalWorkers}
                                remaining={unassignedWorkers}
                            />
                        </Box>
                    </Box>
                </Input.Wrapper>
            </Box>
        </>
    );
};
